import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import PhygitalIcon from "@/components/PhygitalIcon";
import EarlyAccessModal from "@/components/EarlyAccessModal";
import SharedFooter from "@/components/SharedFooter";
import AllCategoriesSection from "@/components/AllCategoriesSection";
import { EditableText, EditableSection, useAdmin } from "@/components/AdminContentEditor";
import { toast } from "sonner";
import { TAP_IN_COVER } from "@/lib/integrationConfig";
import {
  Sparkles,
  Users,
  Heart,
  Utensils,
  Accessibility,
  BarChart3,
  Shield,
  ArrowRight,
  Calendar,
  MapPin,
  Star,
  ChevronRight,
  Share,
  Bell,
  CheckCircle2,
  PartyPopper,
  Wallet,
  QrCode,
  Scan,
  TrendingUp,
  Eye,
  UserCheck,
  MessageCircle,
  Brain,
  Hand,
  Link2,
  DollarSign,
  Car,
  FileText,
  Ticket,
  Music,
  Gift,
  ClipboardList,
  Scissors,
  Paintbrush,
  Palette,
  ShoppingBag,
  Wine
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventsSalesPage = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const { editMode } = useAdmin();
  const [submitted, setSubmitted] = useState(false);
  const [demoTab, setDemoTab] = useState("guest");
  const [showVenuePreview, setShowVenuePreview] = useState(false);
  const [showEarlyAccessModal, setShowEarlyAccessModal] = useState(false);

  useEffect(() => {
    document.title = "phygital - For Event Hosts";
  }, []);
  
  // Sharing toggles for demo
  const [sharingToggles, setSharingToggles] = useState({
    dietary: true,
    accessibility: true,
    invisible: true,
    vibe: true,
    interests: true
  });

  const handleJoinList = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    try {
      await fetch(`${API}/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name, company, source: "events_demo", interest: "event_os" })
      });
    } catch (err) {}
    setSubmitted(true);
    toast.success("You're on the list!");
  };

  // Mock data for demos - simplified, no sensitive details shown
  const mockGuests = [
    { name: "Alex M.", interests: ["Fashion Tech", "Networking"] },
    { name: "Jordan K.", interests: ["AI", "Sustainability"] },
    { name: "Sam T.", interests: ["Design", "Networking"] },
    { name: "Riley P.", interests: ["Fashion Tech", "AI"] },
  ];

  const mockStats = {
    rsvps: 127,
    dietary: 23,
    accessibility: 8,
    checkedIn: 0
  };

  const mockInterests = [
    { name: "Fashion & Design", pct: 72 },
    { name: "Technology & AI", pct: 58 },
    { name: "Networking", pct: 45 },
    { name: "Sustainability", pct: 38 }
  ];

  const mockVibes = [
    { name: "Networking Mode", count: 45, color: "bg-purple-100 text-purple-700" },
    { name: "Just Vibing", count: 38, color: "bg-pink-100 text-pink-700" },
    { name: "Tap In (Music)", count: 28, color: "bg-blue-100 text-blue-700" },
    { name: "Observing", count: 16, color: "bg-gray-100 text-gray-700" }
  ];

  // Tap In app cover image - centralized in integrationConfig.js

  const waysToMakeYours = [
    { icon: Accessibility, label: "Accessibility", desc: "Wheelchair, seating, mobility", isTapIn: false },
    { icon: Utensils, label: "Dietary", desc: "Allergies, restrictions, preferences", isTapIn: false },
    { icon: null, label: "Tap In", desc: "Music preferences and requests", isTapIn: true },
    { icon: Heart, label: "Interests", desc: "Topics you want to discuss", isTapIn: false },
    { icon: MessageCircle, label: "Vibe", desc: "Your social energy level", isTapIn: false },
    { icon: Brain, label: "Invisible Needs", desc: "Conditions others can't see", isTapIn: false }
  ];

  // Event Stack - all the links hosts can add
  const eventStack = [
    { icon: Ticket, label: "Event Booking", desc: "RSVP & ticketing link", color: "bg-purple-100 text-purple-600" },
    { icon: DollarSign, label: "Tips", desc: "Bartender & staff tips", color: "bg-green-100 text-green-600" },
    { icon: Car, label: "Rides", desc: "Rideshare promo codes", color: "bg-blue-100 text-blue-600" },
    { icon: FileText, label: "Resources", desc: "Notion, docs, run of show", color: "bg-amber-100 text-amber-600" },
    { icon: Music, label: "Playlist", desc: "Music requests & queue", color: "bg-pink-100 text-pink-600" },
    { icon: Gift, label: "Sponsors", desc: "Partner offers & CTAs", color: "bg-orange-100 text-orange-600" },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <PhygitalIcon className="w-8 h-8" />
            <span className="font-semibold text-xl">phygital</span>
          </Link>
          <div className="flex items-center gap-4">
            <Badge variant="secondary" className="hidden sm:flex gap-1 bg-purple-100 text-purple-700">
              <Eye className="w-3 h-3" />
              Demo Mode
            </Badge>
            <Button 
              size="sm" 
              className="rounded-full"
              onClick={() => setShowEarlyAccessModal(true)}
              data-testid="events-early-access-btn"
            >
              Get Early Access
              <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <EditableSection sectionId="events-hero" label="Events Hero" className={editMode ? "pt-28 sm:pt-36 pb-12 sm:pb-16 px-4 relative overflow-hidden" : "pt-28 sm:pt-36 pb-12 sm:pb-16 px-4 relative overflow-hidden"}>
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-purple-50/30" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-gradient-to-br from-pink-200/20 to-purple-200/20 rounded-full blur-3xl" />
        
        <div className="relative max-w-6xl mx-auto text-center">
          <Badge className="mb-4 sm:mb-6 bg-gradient-to-r from-pink-500 to-purple-500 text-white border-0">
            <PartyPopper className="w-3 h-3 mr-1" />
            <EditableText sectionId="events-hero" field="badge" defaultValue="Event OS by Phygital" className="inline" />
          </Badge>
          
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
            <EditableText sectionId="events-hero" field="title_line1" defaultValue="Event Management" className="inline" /><br />
            <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 bg-clip-text text-transparent">
              <EditableText sectionId="events-hero" field="title_line2" defaultValue="+ Sponsor Activation" className="inline" />
            </span>
          </h1>
          
          <p className="text-sm sm:text-xl text-gray-700 max-w-3xl mx-auto mb-6 sm:mb-10 px-4">
            <EditableText 
              sectionId="events-hero" 
              field="subtitle" 
              defaultValue="One dashboard for event links, guest preferences, and sponsor tracking. See who's coming and measure engagement in real-time."
              multiline
            />
          </p>
          
          <div className="flex justify-center mb-8 sm:mb-12">
            <Button 
              size="lg" 
              className="rounded-full bg-gray-900 hover:bg-gray-800 text-sm sm:text-base px-6 sm:px-8"
              onClick={() => document.getElementById('demo').scrollIntoView({ behavior: 'smooth' })}
            >
              See Live Demo
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Value Props */}
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap justify-center gap-2 sm:gap-6 text-[11px] sm:text-sm max-w-[300px] sm:max-w-none mx-auto">
            {[
              { icon: Link2, text: "All links in one place" },
              { icon: Users, text: "Guest preferences" },
              { icon: BarChart3, text: "Sponsor analytics" },
              { icon: TrendingUp, text: "Real-time tracking" }
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 text-gray-700 whitespace-nowrap">
                <item.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-500 flex-shrink-0" />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </EditableSection>

      {/* Preference Sharing Layer Section - MOVED UP */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-purple-100 text-purple-700">
              <Heart className="w-3 h-3 mr-1" />
              Preference Sharing Layer
            </Badge>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Know Your Guests Before They Arrive
            </h2>
            <p className="text-gray-700 max-w-xl mx-auto">
              Guests share what matters. You get actionable insights for catering, accessibility, and personalized experiences.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Dietary & Accessibility */}
            <Card className="border-2 border-orange-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                    <Utensils className="w-5 h-5 text-orange-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Dietary & Accessibility</h3>
                </div>
                <div className="space-y-2">
                  {[
                    { label: "Nut-free required", count: 5, color: "bg-red-100 text-red-700" },
                    { label: "Vegan", count: 12, color: "bg-green-100 text-green-700" },
                    { label: "Wheelchair access", count: 3, color: "bg-blue-100 text-blue-700" },
                    { label: "Gluten-free", count: 8, color: "bg-amber-100 text-amber-700" }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-700">{item.label}</span>
                      <Badge className={item.color}>{item.count}</Badge>
                    </div>
                  ))}
                </div>
                <Button size="sm" variant="outline" className="w-full mt-4 text-orange-600 border-orange-200">
                  <Share className="w-3 h-3 mr-2" />
                  Share with Caterer
                </Button>
              </CardContent>
            </Card>

            {/* Invisible Disabilities */}
            <Card className="border-2 border-purple-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Invisible Disabilities</h3>
                </div>
                <p className="text-xs text-gray-600 mb-3">
                  Conditions guests choose to share so staff can better support them.
                </p>
                <div className="space-y-2">
                  {[
                    { condition: "Anxiety", tip: "May need quiet moments", icon: "💭" },
                    { condition: "ADHD", tip: "Prefers shorter interactions", icon: "⚡" },
                    { condition: "Chronic fatigue", tip: "May need seating", icon: "🪑" },
                    { condition: "Sensory sensitivity", tip: "Avoid loud areas", icon: "🔇" }
                  ].map((item, i) => (
                    <div key={i} className="p-2 bg-purple-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span>{item.icon}</span>
                        <span className="text-sm font-medium text-gray-800">{item.condition}</span>
                      </div>
                      <p className="text-xs text-gray-600 ml-6">{item.tip}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Guest Control */}
            <Card className="border-2 border-cyan-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
                    <Eye className="w-5 h-5 text-cyan-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Guest Controls</h3>
                </div>
                <p className="text-xs text-gray-600 mb-3">
                  Guests decide exactly what to share. Privacy-first by design.
                </p>
                <div className="space-y-3">
                  {[
                    { label: "Dietary needs", shared: true },
                    { label: "Accessibility", shared: true },
                    { label: "Invisible conditions", shared: true },
                    { label: "Social vibe", shared: false },
                    { label: "Interests", shared: true }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{item.label}</span>
                      <div className={`w-8 h-5 rounded-full ${item.shared ? 'bg-cyan-500' : 'bg-gray-300'} relative`}>
                        <div className={`absolute top-0.5 ${item.shared ? 'right-0.5' : 'left-0.5'} w-4 h-4 bg-white rounded-full shadow`} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-gray-900 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="w-3 h-3 text-cyan-400" />
                    <span className="text-xs text-white font-medium">What venue sees</span>
                  </div>
                  <p className="text-xs text-gray-500">Vegan, nut allergy, prefers seating near exit, may need quiet moments</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Two Audience Cards - MOVED DOWN */}
      <section className="py-10 sm:py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {/* For Event Hosts */}
            <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white">
              <CardContent className="p-6">
                <Badge className="mb-4 bg-purple-100 text-purple-700">For Event Hosts</Badge>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Manage Everything</h3>
                <p className="text-gray-700 text-sm mb-4">
                  Stop juggling links. Put your booking, tips, rideshare codes, and resources in one place. 
                  Know your guests before they arrive.
                </p>
                <ul className="space-y-2">
                  {[
                    "Aggregate guest dietary & accessibility needs",
                    "Track sponsor link clicks & conversions",
                    "Share vendor summaries with one link",
                    "Real-time RSVP & check-in dashboard"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* For Sponsors */}
            <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white">
              <CardContent className="p-6">
                <Badge className="mb-4 bg-amber-100 text-amber-700">For Sponsors</Badge>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Add Value in 2026</h3>
                <p className="text-gray-700 text-sm mb-4">
                  Get in front of engaged guests at the moment they RSVP. 
                  Your CTA appears alongside event details with trackable engagement.
                </p>
                <ul className="space-y-2">
                  {[
                    "Your offer shown during guest signup",
                    "Track clicks & conversions in real-time",
                    "Audience insights: interests, industries",
                    "Measurable ROI beyond logo placement"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Event Stack Section */}
      <section className="py-12 sm:py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-10">
            <Badge className="mb-4 bg-gray-100 text-gray-700">
              <ClipboardList className="w-3 h-3 mr-1" />
              Your Event Stack
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              All Your Links, One Place
            </h2>
            <p className="text-sm sm:text-base text-gray-700 max-w-2xl mx-auto">
              <span className="block">Manage all your event links from one dashboard.</span>
              <span className="block">Choose what's public-facing for guests.</span>
            </p>
          </div>

          {/* Event Stack Grid - 2 cols on mobile, 3 on desktop */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 max-w-2xl mx-auto mb-6 sm:mb-8">
            {eventStack.map((item, i) => (
              <Card key={i} className="text-center hover:shadow-lg transition-shadow cursor-pointer">
                <CardContent className="p-3 sm:p-4">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 mx-auto rounded-xl ${item.color} flex items-center justify-center mb-2 sm:mb-3`}>
                    <item.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <p className="font-semibold text-gray-900 text-xs sm:text-sm">{item.label}</p>
                  <p className="text-xs text-gray-600 hidden sm:block">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="text-center text-xs sm:text-sm text-gray-600">
            Integrations coming soon. For now, paste any link.
          </p>
        </div>
      </section>

      {/* Interactive Demo */}
      <section id="demo" className="py-12 sm:py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <Badge className="mb-4 bg-purple-100 text-purple-700">
              <Eye className="w-3 h-3 mr-1" />
              Interactive Demo
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              See It In Action
            </h2>
            <p className="text-sm sm:text-base text-gray-700 max-w-2xl mx-auto px-2 sm:whitespace-nowrap">
              Explore both sides of the experience: what guests see and what you see as a host.
            </p>
          </div>

          <Tabs value={demoTab} onValueChange={setDemoTab} className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-6 sm:mb-8">
              <TabsTrigger value="guest" className="gap-1 sm:gap-2 text-xs sm:text-sm">
                <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                Guest View
              </TabsTrigger>
              <TabsTrigger value="host" className="gap-1 sm:gap-2 text-xs sm:text-sm">
                <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4" />
                Host Dashboard
              </TabsTrigger>
            </TabsList>

            {/* Guest View Demo */}
            <TabsContent value="guest">
              <div className="max-w-lg mx-auto">
                <Card className="shadow-xl overflow-hidden">
                  {/* Event Header */}
                  <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-6 sm:p-8 text-center">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto bg-white/10 rounded-2xl flex items-center justify-center mb-3 sm:mb-4">
                      <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">Your Event Name</h3>
                    <p className="text-gray-500 text-sm flex items-center justify-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Event Date
                      <span className="mx-1">·</span>
                      <MapPin className="w-4 h-4" />
                      Location
                    </p>
                  </div>

                  <CardContent className="p-6">
                    {/* Event Stack Quick Links */}
                    <div className="mb-6">
                      <p className="text-xs text-gray-600 font-medium mb-3 text-center">Quick Links</p>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { icon: Ticket, label: "RSVP", color: "bg-purple-50 text-purple-600" },
                          { icon: DollarSign, label: "Tips", color: "bg-green-50 text-green-600" },
                          { icon: Car, label: "Rides", color: "bg-blue-50 text-blue-600" },
                          { icon: FileText, label: "Info", color: "bg-amber-50 text-amber-600" },
                          { icon: Music, label: "Music", color: "bg-pink-50 text-pink-600" },
                          { icon: Gift, label: "Offers", color: "bg-orange-50 text-orange-600" },
                        ].map((item, i) => (
                          <Button key={i} variant="ghost" className={`h-auto py-3 flex-col gap-1 ${item.color}`} disabled>
                            <item.icon className="w-5 h-5" />
                            <span className="text-xs">{item.label}</span>
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Ways to make your experience yours */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3 text-center">
                        Make it yours
                      </h4>
                      <div className="grid grid-cols-3 gap-2">
                        {waysToMakeYours.map((item, i) => (
                          <div key={i} className="p-3 rounded-xl bg-gray-50 text-center hover:bg-gray-100 cursor-pointer transition-colors">
                            {item.isTapIn ? (
                              <div className="w-10 h-10 mx-auto rounded-lg overflow-hidden mb-2 shadow-sm">
                                <img src={TAP_IN_COVER} alt="Tap In" className="w-full h-full object-cover" />
                              </div>
                            ) : (
                              <div className="w-10 h-10 mx-auto rounded-lg bg-gradient-to-br from-pink-100 to-purple-100 flex items-center justify-center mb-2">
                                <item.icon className="w-5 h-5 text-purple-600" />
                              </div>
                            )}
                            <span className="text-xs font-medium text-gray-700 block">{item.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Sharing Controls with Eye Icon */}
                    <div className="p-4 bg-purple-50 rounded-xl mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-medium text-purple-800">Control what you share</p>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-8 px-2 text-purple-600 hover:text-purple-700"
                          onClick={() => setShowVenuePreview(!showVenuePreview)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          {showVenuePreview ? "Hide" : "Preview"}
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {[
                          { key: "dietary", label: "Dietary needs", icon: Utensils },
                          { key: "accessibility", label: "Accessibility", icon: Accessibility },
                          { key: "invisible", label: "Invisible conditions", icon: Brain },
                          { key: "vibe", label: "Social vibe", icon: MessageCircle },
                          { key: "interests", label: "Interests", icon: Heart }
                        ].map((item) => (
                          <div key={item.key} className="flex items-center justify-between py-1">
                            <div className="flex items-center gap-2">
                              <item.icon className="w-4 h-4 text-purple-500" />
                              <span className="text-sm text-gray-700">{item.label}</span>
                            </div>
                            <Switch 
                              checked={sharingToggles[item.key]}
                              onCheckedChange={(checked) => setSharingToggles(prev => ({...prev, [item.key]: checked}))}
                              className="data-[state=checked]:bg-purple-500"
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Venue Preview Modal */}
                    {showVenuePreview && (
                      <div className="p-4 bg-gray-900 rounded-xl mb-6 text-white">
                        <div className="flex items-center gap-2 mb-3">
                          <Eye className="w-4 h-4 text-cyan-400" />
                          <span className="text-sm font-medium">What the venue sees</span>
                        </div>
                        <div className="space-y-2 text-sm">
                          {sharingToggles.dietary && (
                            <div className="flex items-center gap-2">
                              <Utensils className="w-3 h-3 text-orange-400" />
                              <span className="text-gray-300">Vegetarian, Nut Allergy</span>
                            </div>
                          )}
                          {sharingToggles.accessibility && (
                            <div className="flex items-center gap-2">
                              <Accessibility className="w-3 h-3 text-blue-400" />
                              <span className="text-gray-300">Prefers seating near exit</span>
                            </div>
                          )}
                          {sharingToggles.invisible && (
                            <div className="flex items-center gap-2">
                              <Brain className="w-3 h-3 text-purple-400" />
                              <span className="text-gray-300">May need quiet moments</span>
                            </div>
                          )}
                          {sharingToggles.vibe && (
                            <div className="flex items-center gap-2">
                              <MessageCircle className="w-3 h-3 text-pink-400" />
                              <span className="text-gray-300">Networking mode</span>
                            </div>
                          )}
                          {sharingToggles.interests && (
                            <div className="flex items-center gap-2">
                              <Heart className="w-3 h-3 text-red-400" />
                              <span className="text-gray-300">Fashion Tech, AI, Sustainability</span>
                            </div>
                          )}
                          {!Object.values(sharingToggles).some(v => v) && (
                            <p className="text-gray-600 italic">Nothing shared yet</p>
                          )}
                        </div>
                      </div>
                    )}

                    <Button className="w-full rounded-full" disabled>
                      Complete RSVP
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>

                    <p className="text-xs text-center text-gray-600 mt-4">
                      Preferences saved to your Phygital profile for future events
                    </p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Host Dashboard Demo */}
            <TabsContent value="host">
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Stats Column */}
                <div className="space-y-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Overview</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-3 bg-gray-50 rounded-xl">
                          <p className="text-2xl font-bold text-gray-900">{mockStats.rsvps}</p>
                          <p className="text-xs text-gray-600">Total RSVPs</p>
                        </div>
                        <div className="text-center p-3 bg-amber-50 rounded-xl">
                          <p className="text-2xl font-bold text-amber-600">{mockStats.dietary}</p>
                          <p className="text-xs text-gray-600">Dietary Needs</p>
                        </div>
                        <div className="text-center p-3 bg-blue-50 rounded-xl">
                          <p className="text-2xl font-bold text-blue-600">{mockStats.accessibility}</p>
                          <p className="text-xs text-gray-600">Accessibility</p>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded-xl">
                          <p className="text-2xl font-bold text-green-600">{mockStats.checkedIn}</p>
                          <p className="text-xs text-gray-600">Checked In</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Event Stack Management */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Link2 className="w-4 h-4" />
                        Your Links
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {[
                          { label: "RSVP Link", status: "Connected", color: "text-green-600" },
                          { label: "Staff Tips", status: "Add link", color: "text-gray-500" },
                          { label: "Rideshare Code", status: "Add link", color: "text-gray-500" },
                          { label: "Resources", status: "Connected", color: "text-green-600" },
                        ].map((item, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                            <span className="text-sm text-gray-700">{item.label}</span>
                            <span className={`text-xs font-medium ${item.color}`}>{item.status}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Action Items */}
                  <Card className="border-amber-200 bg-amber-50/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                        <Bell className="w-4 h-4" />
                        Action Items
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {[
                          { label: "Nut-free meals needed", count: 5 },
                          { label: "Wheelchair seating", count: 3 },
                          { label: "Vegan options", count: 8 }
                        ].map((item, i) => (
                          <div key={i} className="flex items-center justify-between text-sm">
                            <span className="text-amber-700">{item.label}</span>
                            <Badge className="bg-amber-200 text-amber-800">{item.count}</Badge>
                          </div>
                        ))}
                      </div>
                      <Button size="sm" variant="outline" className="w-full mt-4 border-amber-300 text-amber-700">
                        <Share className="w-3 h-3 mr-2" />
                        Share with Vendors
                      </Button>
                    </CardContent>
                  </Card>
                </div>

                {/* Sponsor Analytics Column - PROMINENT */}
                <div className="space-y-6">
                  {/* Sponsor Tracking - Featured */}
                  <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-white">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2 text-green-800">
                        <BarChart3 className="w-4 h-4" />
                        Sponsor Analytics
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {[
                          { sponsor: "Venue Partner", cta: "Book Your Stay", clicks: 34, conversions: 8 },
                          { sponsor: "Presenting Sponsor", cta: "Learn More", clicks: 28, conversions: 5 },
                          { sponsor: "Gold Sponsor", cta: "Shop Now", clicks: 19, conversions: 3 }
                        ].map((item, i) => (
                          <div key={i} className="p-3 bg-white rounded-lg border border-green-100">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-800">{item.sponsor}</span>
                              <Badge className="bg-green-100 text-green-700 text-xs">{item.cta}</Badge>
                            </div>
                            <div className="flex items-center gap-4 text-xs">
                              <div className="flex items-center gap-1">
                                <Eye className="w-3 h-3 text-gray-500" />
                                <span className="text-gray-700">{item.clicks} clicks</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3 text-green-500" />
                                <span className="text-green-600 font-medium">{item.conversions} conversions</span>
                              </div>
                            </div>
                            <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-green-400 to-green-500 rounded-full"
                                style={{ width: `${(item.conversions / item.clicks) * 100}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-green-600 mt-4 font-medium text-center">
                        Measurable ROI for every sponsor
                      </p>
                    </CardContent>
                  </Card>

                  {/* Guest Interests */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Guest Interests
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {mockInterests.map((interest, i) => (
                          <div key={i}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-700">{interest.name}</span>
                              <span className="text-gray-600">{interest.pct}%</span>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-pink-500 to-purple-500 rounded-full"
                                style={{ width: `${interest.pct}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-600 mt-4">
                        Share with sponsors for targeting
                      </p>
                    </CardContent>
                  </Card>

                  {/* Team Access */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Team Access
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {[
                          { role: "Owner", access: "Full access", color: "bg-purple-100 text-purple-700" },
                          { role: "Caterer", access: "Dietary only", color: "bg-orange-100 text-orange-700" },
                          { role: "Sponsor", access: "Analytics only", color: "bg-green-100 text-green-700" }
                        ].map((member, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium">{member.role}</span>
                            <Badge className={member.color}>{member.access}</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Guest List Column */}
                <div>
                  <Card className="h-full">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Recent RSVPs
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {mockGuests.map((guest, i) => (
                          <div key={i} className="p-3 bg-gray-50 rounded-xl">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
                                {guest.name.charAt(0)}
                              </div>
                              <div className="flex-1">
                                <span className="font-medium text-sm block">{guest.name}</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {guest.interests.slice(0, 2).map((interest, j) => (
                                    <Badge key={j} variant="secondary" className="text-xs bg-gray-200 text-gray-700">{interest}</Badge>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <Button variant="outline" className="w-full mt-4" disabled>
                        View All Guests
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Check-in Experience */}
      <section className="py-12 sm:py-16 px-4 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <Badge className="mb-4 bg-white/10 text-white border-0">
              <Sparkles className="w-3 h-3 mr-1" />
              Coming Soon
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Frictionless Check-In
            </h2>
            <p className="text-sm sm:text-base text-gray-300 max-w-xl mx-auto px-2">
              Digital wallet passes and NFC readers for instant, seamless event entry.
            </p>
          </div>

          {/* 3 columns on all screens, smaller on mobile */}
          <div className="grid grid-cols-3 gap-2 sm:gap-8 max-w-4xl mx-auto">
            {/* Step 1: Wallet Pass */}
            <div className="text-center">
              <div className="relative mb-3 sm:mb-6">
                <div className="w-20 h-28 sm:w-48 sm:h-64 mx-auto bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl sm:rounded-2xl p-2 sm:p-4 shadow-xl sm:shadow-2xl">
                  <div className="h-full bg-white/10 rounded-lg sm:rounded-xl flex flex-col items-center justify-center">
                    <Wallet className="w-5 h-5 sm:w-10 sm:h-10 text-white mb-1 sm:mb-3" />
                    <p className="text-white font-medium text-[8px] sm:text-sm">Digital Pass</p>
                    <div className="w-8 h-8 sm:w-16 sm:h-16 mt-1 sm:mt-4 bg-white rounded sm:rounded-lg flex items-center justify-center">
                      <QrCode className="w-6 h-6 sm:w-12 sm:h-12 text-gray-900" />
                    </div>
                  </div>
                </div>
              </div>
              <h3 className="text-white font-semibold text-xs sm:text-base mb-1 sm:mb-2">1. Add to Wallet</h3>
              <p className="text-gray-500 text-[10px] sm:text-sm hidden sm:block">Guests save their pass to Apple or Google Wallet</p>
            </div>

            {/* Step 2: Tap */}
            <div className="text-center">
              <div className="relative mb-3 sm:mb-6">
                <div className="w-20 h-28 sm:w-48 sm:h-64 mx-auto flex items-center justify-center">
                  <div className="relative">
                    <div className="w-14 h-20 sm:w-32 sm:h-44 bg-gradient-to-br from-gray-700 to-gray-800 rounded-lg sm:rounded-xl shadow-xl sm:shadow-2xl flex flex-col items-center justify-center">
                      <Scan className="w-6 h-6 sm:w-12 sm:h-12 text-cyan-400 mb-1 sm:mb-2" />
                      <p className="text-gray-300 text-[8px] sm:text-xs">Tap to Enter</p>
                    </div>
                    <div className="absolute -inset-2 sm:-inset-4 border sm:border-2 border-cyan-400/30 rounded-xl sm:rounded-2xl animate-pulse" />
                  </div>
                </div>
              </div>
              <h3 className="text-white font-semibold text-xs sm:text-base mb-1 sm:mb-2">2. Tap In</h3>
              <p className="text-gray-500 text-[10px] sm:text-sm hidden sm:block">Quick tap on NFC reader at entry</p>
            </div>

            {/* Step 3: Confirmed */}
            <div className="text-center">
              <div className="relative mb-3 sm:mb-6">
                <div className="w-20 h-28 sm:w-48 sm:h-64 mx-auto bg-white rounded-xl sm:rounded-2xl p-2 sm:p-4 shadow-xl sm:shadow-2xl">
                  <div className="h-full flex flex-col">
                    <div className="flex items-center gap-1 sm:gap-2 mb-2 sm:mb-4 pb-1 sm:pb-3 border-b">
                      <PhygitalIcon className="w-3 h-3 sm:w-5 sm:h-5" />
                      <span className="font-medium text-[6px] sm:text-sm">Live Check-in</span>
                    </div>
                    <div className="flex-1 flex flex-col justify-center">
                      <div className="w-8 h-8 sm:w-16 sm:h-16 mx-auto mb-1 sm:mb-3 rounded-full bg-green-100 flex items-center justify-center">
                        <UserCheck className="w-4 h-4 sm:w-8 sm:h-8 text-green-600" />
                      </div>
                      <p className="font-semibold text-gray-900 text-[10px] sm:text-base">Checked In!</p>
                      <p className="text-[8px] sm:text-sm text-gray-600 hidden sm:block">Welcome, Guest</p>
                    </div>
                  </div>
                </div>
              </div>
              <h3 className="text-white font-semibold text-xs sm:text-base mb-1 sm:mb-2">3. Confirmed</h3>
              <p className="text-gray-500 text-[10px] sm:text-sm hidden sm:block">Real-time dashboard updates</p>
            </div>
          </div>
        </div>
      </section>

      {/* Waitlist */}
      <section id="waitlist" className="py-12 sm:py-16 px-4 bg-gray-50">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-8 sm:mb-10">
            <Badge className="mb-4 bg-gradient-to-r from-pink-500 to-purple-500 text-white border-0">
              <Star className="w-3 h-3 mr-1" />
              Early Access
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Be First to Use It Live
            </h2>
            <p className="text-sm sm:text-base text-gray-700 px-4">
              <span className="sm:inline">Join our early access list. </span>
              <span className="block sm:inline">We are onboarding select events.</span>
            </p>
          </div>

          <Card className="text-center">
            <CardContent className="py-8 sm:py-10">
              <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 rounded-full bg-purple-100 flex items-center justify-center">
                <PartyPopper className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Ready to get started?</h3>
              <p className="text-sm sm:text-base text-gray-700 mb-6">Fill out our quick form and we'll be in touch.</p>
              <Button 
                size="lg" 
                className="rounded-full bg-gray-900 hover:bg-gray-800 px-8"
                onClick={() => setShowEarlyAccessModal(true)}
                data-testid="events-cta-early-access-btn"
              >
                Get Early Access
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Built for Every Service Industry - Hidden for now */}
      {/* <AllCategoriesSection currentCategory="event" /> */}

      {/* Footer */}
      <SharedFooter />
      
      {/* Early Access Modal */}
      <EarlyAccessModal open={showEarlyAccessModal} onOpenChange={setShowEarlyAccessModal} category="events" />
    </div>
  );
};

export default EventsSalesPage;
